import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Trash2, RefreshCw, Database, Zap, Music4, Bot, ExternalLink, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminMusicBank() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/music-bank/stats"),
        api.get("/admin/music-bank", { params: { q: query, limit: 200 } }),
      ]);
      setStats(s.data); setRows(r.data || []);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(""); }, []);

  const onSearch = (e) => { e?.preventDefault?.(); load(q.trim()); };

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/admin/music-bank/${id}`); setDetail(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Remover esta música do banco global? Ela poderá ser reconsultada na web futuramente.")) return;
    try { await api.delete(`/admin/music-bank/${id}`); toast.success("Removida do banco global."); load(q.trim()); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const cards = useMemo(() => ([
    { label: "Músicas no banco", value: stats?.total_songs ?? "—", Icon: Music4 },
    { label: "Total de consultas", value: stats?.total_hits ?? "—", Icon: Database },
    { label: "Buscas externas economizadas", value: stats?.external_fetches_saved ?? "—", Icon: Zap },
  ]), [stats]);

  const fmt = (d) => { try { return d ? new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—"; } catch { return "—"; } };

  return (
    <div className="space-y-5" data-testid="admin-music-bank">
      <div>
        <h2 className="font-head text-xl font-bold flex items-center gap-2"><Database className="h-5 w-5 text-[#FF4D00]" />Banco Global de Músicas</h2>
        <p className="text-sm text-muted-foreground mt-1">Cada cifra consultada é armazenada aqui (versão original). Novas buscas reutilizam este acervo antes de ir à web. Edições dos usuários não afetam estas versões.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {cards.map((c) => (
          <div key={c.label} data-testid={`mb-stat-${c.label}`} className="rounded-2xl border border-border bg-surface p-4 flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-[#FF4D00]/15 text-[#FF4D00] shrink-0"><c.Icon className="h-5 w-5" /></span>
            <div><div className="text-2xl font-extrabold tabular-nums">{c.value}</div><div className="text-xs text-muted-foreground">{c.label}</div></div>
          </div>
        ))}
      </div>

      <form onSubmit={onSearch} className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="mb-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por título, artista ou URL..." className="bg-background border-border rounded-xl pl-9" />
        </div>
        <Button type="submit" data-testid="mb-search-btn" variant="outline" className="rounded-xl bg-transparent">Buscar</Button>
        <Button type="button" data-testid="mb-refresh" variant="outline" onClick={() => { setQ(""); load(""); }} className="rounded-xl bg-transparent gap-1.5"><RefreshCw className="h-4 w-4" />Atualizar</Button>
      </form>

      <div className="rounded-2xl border border-border bg-surface overflow-hidden">
        <div className="hidden sm:grid grid-cols-[1fr_90px_90px_110px_80px] gap-2 px-4 py-2.5 text-xs font-semibold text-muted-foreground border-b border-border">
          <span>Música</span><span className="text-center">Tom</span><span className="text-center">Consultas</span><span className="text-center">Último uso</span><span className="text-right">Ações</span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Carregando...</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-sm flex flex-col items-center gap-2"><Database className="h-7 w-7 opacity-60" />Nenhuma música no banco global ainda.</div>
        ) : rows.map((r) => (
          <div key={r.id} data-testid={`mb-row-${r.id}`} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_90px_90px_110px_80px] gap-2 items-center px-4 py-3 border-b border-border last:border-0 hover:bg-surface-hover transition-colors">
            <button onClick={() => openDetail(r.id)} data-testid={`mb-open-${r.id}`} className="min-w-0 text-left">
              <div className="font-semibold text-sm truncate flex items-center gap-1.5">{r.title || "—"} {r.ai_generated && <Bot className="h-3.5 w-3.5 text-primary shrink-0" title="Gerada por IA" />}</div>
              <div className="text-xs text-muted-foreground truncate">{r.artist || "—"} · {r.source || "web"}</div>
            </button>
            <span className="hidden sm:block text-center text-xs font-mono">{r.key || "—"}</span>
            <span className="hidden sm:block text-center"><span className="inline-flex items-center justify-center min-w-8 px-2 py-0.5 rounded-full bg-[#FF4D00]/10 text-[#FF4D00] text-xs font-bold tabular-nums">{r.hits}</span></span>
            <span className="hidden sm:block text-center text-xs text-muted-foreground">{fmt(r.last_used_at)}</span>
            <div className="flex items-center justify-end gap-1">
              {r.url && <a href={r.url} target="_blank" rel="noreferrer" data-testid={`mb-src-${r.id}`} className="h-8 w-8 grid place-items-center rounded-md text-muted-foreground hover:text-foreground" title="Abrir fonte"><ExternalLink className="h-4 w-4" /></a>}
              <button onClick={() => del(r.id)} data-testid={`mb-del-${r.id}`} className="h-8 w-8 grid place-items-center rounded-md text-muted-foreground hover:text-destructive" title="Remover"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="mb-detail">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Music4 className="h-5 w-5 text-accent" />{detail?.title}{detail?.key ? ` · ${detail.key}` : ""}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>Artista: <b className="text-foreground">{detail.artist || "—"}</b></span>
                <span>Fonte: <b className="text-foreground">{detail.source || "—"}</b></span>
                <span>Consultas: <b className="text-[#FF4D00]">{detail.hits}</b></span>
                {detail.ai_generated && <span className="text-primary">Gerada por IA</span>}
              </div>
              {(detail.content?.sections || []).length > 0 ? (
                <div className="rounded-xl border border-border bg-background p-3 space-y-3 font-mono text-xs">
                  {(detail.content.sections || []).map((s, i) => (
                    <div key={i}>
                      {s.title && <div className="text-accent font-semibold mb-0.5">[{s.title}]</div>}
                      <pre className="whitespace-pre-wrap leading-relaxed">{(s.lines || []).map((l) => (typeof l === "string" ? l : `${l.chords || ""}\n${l.lyric || l.text || ""}`)).join("\n")}</pre>
                    </div>
                  ))}
                </div>
              ) : <pre className="whitespace-pre-wrap font-mono text-xs bg-background border border-border rounded-xl p-3">{detail.content?.raw_text || "Sem conteúdo."}</pre>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
